import React, { useState, useEffect, FormEvent, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronDown, ChevronUp, X, ChevronRight, CheckCircle, FileText, Calendar, Users, DollarSign, CreditCard, RefreshCcw } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { sendProposalLink, setProposalServiceSelection, ProposalService, logProposalEvent, logProposalView } from '../lib/proposals';
import { createProposalViewedMilestone, createProposalApprovedMilestone } from '../lib/bookingMilestones';
import { useSupabase } from '../contexts/SupabaseContext';
import { useRouter } from 'next/router';
import { formatCurrency } from '../lib/utils';
import RichTextDescription from '../components/RichTextDescription';

interface ProposalToken {
  proposal_id: string;
  business_id: string;
  token: string;
  expires_at: string;
  created_at: string;
}

interface Business {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  logo_url?: string;
}

interface Customer {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

interface Service {
  id?: string;
  name: string;
  description: string;
  price: number;
  quantity: number;
  title: string;
  details?: string[];
  benefits?: string[];
  estimated_duration?: string;
  isSelectable?: boolean;
  is_selected?: boolean;
  isSelected?: boolean;
  offer?: {
    id: string;
    title: string;
    status: string;
  };
}

interface Proposal {
  id: string;
  business_id: string;
  customer_id: string;
  title: string;
  description: string;
  total_amount: number;
  status: 'draft' | 'sent' | 'viewed' | 'approved' | 'declined';
  valid_until: string;
  created_at: string;
  updated_at: string;
  services: Service[];
  customer: Customer;
  notes?: string;
}

interface OfferProps {
  title: string;
  services: Service[];
  isExpanded: boolean;
  isSelected: boolean;
  onToggle: () => void;
  onSelect: () => void;
  tax?: number;
  promo?: number;
}

const Offer: React.FC<OfferProps> = ({
  title,
  services,
  isExpanded,
  isSelected,
  onToggle,
  onSelect,
  tax = 0.05,
  promo = 0
}) => {
  // No local state for optional service selection - use database state directly
  // Add state for tracking service selection locally
  const [localServiceToggle, setLocalServiceToggle] = useState<Record<string, boolean>>({});
  
  // Calculate totals based on database selection state AND local toggle state
  const subtotal = services.reduce((total, service) => {
    // Only include the service in the total if:
    // 1. It's not optional (isSelectable === false), OR
    // 2. It's optional AND selected in the database (is_selected === true) or local state
    const isOptionalByName = service.name?.includes('(Optional)') || false;
    let isSelectable = Boolean(service.isSelectable);
    
    // If the service name indicates it's optional but isSelectable isn't set, force it to true
    if (isOptionalByName && !isSelectable) {
      isSelectable = true;
    }
    
    // Check both database state and local toggle state
    const serviceId = service.id || `service-${services.indexOf(service)}`;
    const isSelected = service.is_selected === true || localServiceToggle[serviceId] === true;
    const includeInTotal = !isSelectable || (isSelectable && isSelected);
    
    if (!includeInTotal) {
      return total;
    }
    
    const price = typeof service.price === 'number' ? service.price : 0;
    const quantity = typeof service.quantity === 'number' ? service.quantity : 1;
    return total + (price * quantity);
  }, 0);
  
  const taxAmount = Math.round((subtotal * tax) * 100) / 100;
  const total = Math.round((subtotal - promo + taxAmount) * 100) / 100;

  // Format currency with fallback
  const formatCurrency = (value: number) => {
    if (isNaN(value)) return '$0.00';
    return `$${value.toFixed(2)}`;
  };

  return (
    <div 
      className={`
        relative overflow-hidden
        bg-gradient-to-br from-white/80 to-white/90
        backdrop-blur-xl
        border border-white/20
        rounded-xl sm:rounded-2xl
        transition-all duration-300
        group
        ${isExpanded ? 'shadow-2xl shadow-white/20' : 'hover:shadow-xl hover:shadow-white/20'}
        ${isSelected ? 'ring-2 ring-blue-500/50 ring-offset-2 ring-offset-black/50' : ''}
        animate-pulse-subtle
        text-[13px] sm:text-base
      `}
    >
      <div 
        className={`
          cursor-pointer transition-all duration-300
          ${isExpanded ? 'bg-white/10' : 'hover:bg-white/5'}
        `}
        onClick={onToggle}
      >
        <div className="flex items-center justify-between p-3 sm:p-5 gap-3 sm:gap-6">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="text-gray-700 bg-gray-100 rounded-full p-1.5 sm:p-2 shadow-inner border border-gray-200">
              {isExpanded ? <ChevronUp className="h-4 w-4 sm:h-5 sm:w-5" /> : <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5" />}
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 font-orbitron">{title}</h3>
            </div>
          </div>
          <div className="flex items-center gap-3 sm:gap-6">
            <div className="text-right">
              <p className="text-lg sm:text-2xl font-bold text-[#00CC4D] font-orbitron">{formatCurrency(total)}</p>
              <p className="text-xs sm:text-sm text-gray-600">inc. GST</p>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSelect();
              }}
              className={`
                relative px-4 sm:px-7 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium
                transition-all duration-300 font-orbitron
                before:absolute before:inset-0 before:rounded-full before:bg-gradient-to-r
                before:transition-all before:duration-300 before:opacity-90
                ${isSelected 
                  ? 'before:from-[#00CC4D] before:to-[#00B344] text-white shadow-lg shadow-[#00CC4D]/30 scale-95 ring-1 sm:ring-2 ring-[#00CC4D]/50 ring-offset-1 sm:ring-offset-2 ring-offset-black/50'
                  : 'before:from-[#00CC4D] before:to-[#00B344] text-white hover:scale-105 hover:shadow-lg hover:shadow-[#00CC4D]/30'
                }
                overflow-hidden group whitespace-nowrap
              `}
            >
              <span className="relative inline-flex items-center">
                {isSelected ? 'SELECTED' : 'SELECT'}
              </span>
            </button>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-3 sm:px-6 pb-4 sm:pb-5 border-t border-gray-200">
          <div className="pt-1 sm:pt-2">
            <div className="space-y-2 sm:space-y-3">
              <div className="overflow-x-auto -mx-3 sm:mx-0">
                <table className="w-full min-w-[350px] sm:min-w-[500px]">
                  <thead>
                    <tr className="text-[13px] sm:text-[15px] text-gray-600 border-b border-gray-200">
                      <th className="text-left pb-1.5 font-medium pl-3 sm:pl-0 w-[45%]">Line Item</th>
                      <th className="text-center pb-1.5 font-medium w-[15%]">Qty</th>
                      <th className="text-right pb-1.5 font-medium w-[20%]">Unit Price</th>
                      <th className="text-right pb-1.5 font-medium pr-3 sm:pr-0 w-[20%]">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Show main services first in the table */}
                    {services
                      .filter(service => {
                        const isOptionalByName = service.name?.includes('(Optional)') || false;
                        let isSelectable = Boolean(service.isSelectable);
                        // If the service name indicates it's optional but isSelectable isn't set, force it to true
                        if (isOptionalByName && !isSelectable) {
                          isSelectable = true;
                        }
                        return !isSelectable;
                      })
                      .map(service => {
                        const serviceId = service.id || `service-${services.indexOf(service)}`;
                        return (
                          <tr key={`main-table-${serviceId}`} className="text-[13px] sm:text-[15px] leading-relaxed text-gray-700">
                            <td className="py-1.5 sm:py-2 pl-3 sm:pl-0 pr-2">
                              {service.name.replace(/ \(Optional\)/gi, '')}
                            </td>
                            <td className="text-center">{typeof service.quantity === 'number' ? service.quantity : 1}</td>
                            <td className="text-right pl-2">{formatCurrency(typeof service.price === 'number' ? service.price : 0)}</td>
                            <td className="text-right pr-3 sm:pr-0">{formatCurrency(typeof service.price === 'number' ? service.price * (typeof service.quantity === 'number' ? service.quantity : 1) : 0)}</td>
                          </tr>
                        );
                      })
                    }
                    
                    {/* Then show selected optional services */}
                    {services
                      .filter(service => {
                        const isOptionalByName = service.name?.includes('(Optional)') || false;
                        let isSelectable = Boolean(service.isSelectable);
                        // If the service name indicates it's optional but isSelectable isn't set, force it to true
                        if (isOptionalByName && !isSelectable) {
                          isSelectable = true;
                        }
                        const isSelected = service.is_selected === true || 
                          localServiceToggle[service.id || `service-${services.indexOf(service)}`] === true;
                        return isSelectable && isSelected;
                      })
                      .map(service => {
                        const serviceId = service.id || `service-${services.indexOf(service)}`;
                        const isSelected = service.is_selected === true || localServiceToggle[serviceId] === true;
                        
                        return (
                          <tr key={`optional-table-${serviceId}`} className="text-[13px] sm:text-[15px] leading-relaxed text-gray-700">
                            <td className="py-1.5 sm:py-2 pl-3 sm:pl-0 pr-2">
                              {service.name.replace(/ \(Optional\)/gi, '')}
                            </td>
                            <td className="text-center">{typeof service.quantity === 'number' ? service.quantity : 1}</td>
                            <td className={`text-right pl-2 ${!isSelected ? 'text-gray-400' : ''}`}>{formatCurrency(typeof service.price === 'number' ? service.price : 0)}</td>
                            <td className={`text-right pr-3 sm:pr-0 ${!isSelected ? 'text-gray-400' : ''}`}>{formatCurrency(typeof service.price === 'number' ? service.price * (typeof service.quantity === 'number' ? service.quantity : 1) : 0)}</td>
                          </tr>
                        );
                      })
                    }
                  </tbody>
                </table>
              </div>

              {/* Add spacing between table and services details */}
              <div className="mt-6 mb-4 border-b border-gray-200/50"></div>

              {/* Display non-selectable (main) services first */}
              {services
                .filter(service => !service.isSelectable && !service.name?.includes('(Optional)'))
                .map((service, index) => {
                  const serviceId = service.id || `service-${services.indexOf(service)}`;
                  return (
                    <div key={`main-${serviceId}`} className="pt-3 sm:pt-4 pb-3 sm:pb-4 border-t border-gray-200/50">
                      <div className="flex items-start justify-between mb-2 sm:mb-3">
                        <div className="flex-1">
                          <h4 className="font-medium text-[13px] sm:text-[15px] font-orbitron text-gray-800">
                            {service.name.replace(/ \(Optional\)/gi, '')}
                          </h4>
                        </div>
                        <div className="flex items-center">
                          <div className="text-sm font-semibold mr-3 text-[#00CC4D]">
                            {formatCurrency(typeof service.price === 'number' ? service.price : 0)}
                          </div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                        {service.description && (
                          <div className="flex items-start gap-1.5 sm:gap-2 text-[13px] sm:text-[15px] leading-relaxed col-span-full mb-3 sm:mb-4">
                            <div className="w-1 sm:w-1.5 h-1 sm:h-1.5 rounded-full mt-2 opacity-80 flex-shrink-0 bg-[#00CC4D]" />
                            <div className="text-gray-600">
                              <RichTextDescription 
                                description={service.description} 
                                className="prose-sm text-gray-600 prose-headings:text-gray-700 prose-strong:text-gray-700 prose-em:text-gray-500" 
                              />
                            </div>
                          </div>
                        )}
                        {service.details && service.details.map((detail, detailIndex) => (
                          <div key={detailIndex} className="flex items-start gap-1.5 sm:gap-2 text-[13px] sm:text-[15px] leading-relaxed">
                            <div className="w-1 sm:w-1.5 h-1 sm:h-1.5 rounded-full mt-2 opacity-80 flex-shrink-0 bg-[#00CC4D]" />
                            <p className="text-gray-600">{detail}</p>
                          </div>
                        ))}
                      </div>

                      {service.benefits && service.benefits.length > 0 && (
                        <div className="mt-4 sm:mt-5">
                          <h5 className="font-medium mb-2 sm:mb-3 text-[12px] sm:text-[14px] font-orbitron text-gray-800">Key Benefits</h5>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                            {service.benefits.map((benefit, benefitIndex) => (
                              <div key={benefitIndex} className="flex items-start gap-1.5 sm:gap-2 text-[13px] sm:text-[15px] leading-relaxed">
                                <div className="w-1 sm:w-1.5 h-1 sm:h-1.5 rounded-full mt-2 opacity-80 flex-shrink-0 bg-[#00CC4D]" />
                                <p className="text-gray-600">{benefit}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {service.estimated_duration && (
                        <div className="mt-4 sm:mt-5">
                          <h5 className="font-medium mb-2 sm:mb-3 text-[12px] sm:text-[14px] font-orbitron text-gray-800">Estimated Duration</h5>
                          <p className="text-[13px] sm:text-[15px] leading-relaxed text-gray-600">{service.estimated_duration}</p>
                        </div>
                      )}
                    </div>
                  );
                })
              }
              
              {/* Add the separator for optional services if we have both types */}
              {services.some(s => !s.isSelectable && !s.name?.includes('(Optional)')) && 
               services.some(s => s.isSelectable || s.name?.includes('(Optional)')) && (
                <div className="border-t border-gray-200/80 mt-4 pt-4 mb-3">
                  <div className="text-center px-3 py-2 bg-[#e8f5e9] rounded-md border border-[#c8e6c9] text-sm font-medium text-gray-700 shadow-sm">
                    <span className="inline-flex items-center">
                      Select Any Additional Services Below Before Selecting An Offer
                    </span>
                  </div>
                </div>
              )}
              
              {/* Then display all optional services */}
              {services
                .filter(service => service.isSelectable || service.name?.includes('(Optional)'))
                .map((service, index) => {
                  const serviceId = service.id || `service-${services.indexOf(service)}`;
                  const isSelected = service.is_selected === true || localServiceToggle[serviceId] === true;
                  
                  // Only hide optional services that aren't selected when the offer is expanded
                  if (!isSelected && !isExpanded) {
                    return null;
                  }
                  
                  return (
                    <div key={`optional-${serviceId}`} className="pt-3 sm:pt-4 pb-3 sm:pb-4 border-t border-gray-200/50">
                      <div className="flex items-start justify-between mb-2 sm:mb-3">
                        <div className="flex-1">
                          <h4 className="font-medium text-[13px] sm:text-[15px] font-orbitron text-gray-800">
                            {service.name.replace(/ \(Optional\)/gi, '')}
                          </h4>
                        </div>
                        <div className="flex items-center">
                          <div className={`text-sm font-semibold mr-3 ${!isSelected ? 'text-gray-400' : 'text-[#00CC4D]'}`}>
                            {formatCurrency(typeof service.price === 'number' ? service.price : 0)}
                          </div>
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log(`Toggle clicked for ${service.name}`);
                              if (supabase && serviceId) {
                                const newState = !isSelected;
                                console.log(`Updating service ${serviceId} to ${newState}`);
                                supabase
                                  .from('proposal_services')
                                  .update({ is_selected: newState })
                                  .eq('id', serviceId)
                                  .then(result => {
                                    console.log("Toggle result:", result);
                                    if (!result.error) {
                                      // Update the local state instead of reloading the page
                                      service.is_selected = newState;
                                      // Force re-render by updating a state variable
                                      setLocalServiceToggle(prev => ({
                                        ...prev,
                                        [serviceId]: newState
                                      }));
                                    } else {
                                      console.error("Error toggling service:", result.error);
                                    }
                                  });
                              }
                            }}
                            className={`
                              relative w-12 h-6 rounded-full p-1 flex transition-all duration-300 shadow-inner
                              ${isSelected ? 'bg-[#00CC4D] justify-end' : 'bg-white justify-start'}
                              border-2 ${isSelected ? 'border-[#00CC4D]' : 'border-[#00CC4D]/40'}
                              cursor-pointer hover:border-[#00CC4D] hover:shadow-md
                              outline outline-2 outline-offset-2 ${isSelected ? 'outline-[#00CC4D]/20' : 'outline-[#00CC4D]/10'}
                            `}
                            title={isSelected ? "Click to deselect" : "Click to select"}
                          >
                            <div className={`
                              w-4 h-4 rounded-full transition-all duration-300 flex items-center justify-center
                              ${isSelected ? 'bg-white text-[#00CC4D]' : 'bg-[#00CC4D]/40 text-white'}
                              hover:bg-[#00CC4D]
                            `}>
                              {isSelected && <span className="text-xs">✓</span>}
                            </div>
                          </button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                        {service.description && (
                          <div className="flex items-start gap-1.5 sm:gap-2 text-[13px] sm:text-[15px] leading-relaxed col-span-full mb-3 sm:mb-4">
                            <div className={`w-1 sm:w-1.5 h-1 sm:h-1.5 rounded-full mt-2 opacity-80 flex-shrink-0 ${!isSelected ? 'bg-gray-300' : 'bg-[#00CC4D]'}`} />
                            <div className="text-gray-600">
                              <RichTextDescription 
                                description={service.description} 
                                className="prose-sm text-gray-600 prose-headings:text-gray-700 prose-strong:text-gray-700 prose-em:text-gray-500" 
                              />
                            </div>
                          </div>
                        )}
                        {service.details && service.details.map((detail, detailIndex) => (
                          <div key={detailIndex} className="flex items-start gap-1.5 sm:gap-2 text-[13px] sm:text-[15px] leading-relaxed">
                            <div className={`w-1 sm:w-1.5 h-1 sm:h-1.5 rounded-full mt-2 opacity-80 flex-shrink-0 ${!isSelected ? 'bg-gray-300' : 'bg-[#00CC4D]'}`} />
                            <p className="text-gray-600">{detail}</p>
                          </div>
                        ))}
                      </div>

                      {service.benefits && service.benefits.length > 0 && (
                        <div className="mt-4 sm:mt-5">
                          <h5 className="font-medium mb-2 sm:mb-3 text-[12px] sm:text-[14px] font-orbitron text-gray-800">Key Benefits</h5>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                            {service.benefits.map((benefit, benefitIndex) => (
                              <div key={benefitIndex} className="flex items-start gap-1.5 sm:gap-2 text-[13px] sm:text-[15px] leading-relaxed">
                                <div className={`w-1 sm:w-1.5 h-1 sm:h-1.5 rounded-full mt-2 opacity-80 flex-shrink-0 ${!isSelected ? 'bg-gray-300' : 'bg-[#00CC4D]'}`} />
                                <p className="text-gray-600">{benefit}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {service.estimated_duration && (
                        <div className="mt-4 sm:mt-5">
                          <h5 className="font-medium mb-2 sm:mb-3 text-[12px] sm:text-[14px] font-orbitron text-gray-800">Estimated Duration</h5>
                          <p className="text-[13px] sm:text-[15px] leading-relaxed text-gray-600">{service.estimated_duration}</p>
                        </div>
                      )}
                    </div>
                  );
                })
              }

              <div className="border-t border-gray-200/80 pt-4 sm:pt-5 mt-4">
                <div className="space-y-2 sm:space-y-3">
                  <div className="flex justify-between text-[13px] sm:text-[15px] text-gray-600">
                    <span>Subtotal</span>
                    <span>{formatCurrency(subtotal)}</span>
                  </div>
                  {promo > 0 && (
                    <div className="flex justify-between text-[13px] sm:text-[15px] text-blue-600">
                      <span>Promo</span>
                      <span>-{formatCurrency(promo)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-[13px] sm:text-[15px] text-gray-600">
                    <span>GST ({(tax * 100).toFixed(0)}%)</span>
                    <span>+{formatCurrency(taxAmount)}</span>
                  </div>
                  <div className="flex justify-between text-base sm:text-lg font-bold pt-3 mt-1 border-t border-gray-200/50">
                    <span className="text-gray-800 font-orbitron">Total</span>
                    <span className="text-gray-800 font-orbitron">{formatCurrency(total)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = `
  @keyframes pulse-subtle {
    0% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.2); }
    70% { box-shadow: 0 0 0 10px rgba(255, 255, 255, 0); }
    100% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0); }
  }
  
  .animate-pulse-subtle {
    animation: pulse-subtle 3s infinite;
  }
`;

const styleSheet = document.createElement('style');
styleSheet.textContent = styles;
document.head.appendChild(styleSheet);

// Add this utility function at the top level of the file or inside the component
const fetchSelectedServicesForProposal = async (supabaseClient: any, proposalId: string) => {
  if (!supabaseClient) return null;
  
  try {
    // First, find the approved offer ID
    const { data: proposalData, error: proposalError } = await supabaseClient
      .from('proposals')
      .select('notes')
      .eq('id', proposalId)
      .single();
      
    if (proposalError || !proposalData?.notes) {
      console.error("Error fetching proposal notes:", proposalError);
      return null;
    }
    
    // Parse the notes to get the approved offer ID
    let approvedOfferId: string | null = null;
    try {
      const notes = JSON.parse(proposalData.notes);
      approvedOfferId = notes.approved_offer_id;
    } catch (e) {
      console.error("Error parsing notes:", e);
    }
    
    if (!approvedOfferId) {
      console.error("No approved offer ID found in notes");
      return null;
    }
    
    // Fetch all services for this proposal and offer that are selected
    const { data: servicesData, error: servicesError } = await supabaseClient
      .from('proposal_services_with_details')
      .select('*')
      .eq('proposal_id', proposalId)
      .eq('is_selected', true);
      
    if (servicesError) {
      console.error("Error fetching selected services:", servicesError);
      return null;
    }
    
    console.log(`Found ${servicesData.length} selected services for proposal ${proposalId}`);
    
    // Make sure all required properties are available for display
    const processedServices = servicesData.map((service: any) => ({
      id: service.id,
      name: service.name,
      description: service.description,
      price: typeof service.price === 'number' ? service.price : 0,
      quantity: typeof service.quantity === 'number' ? service.quantity : 1,
      is_selected: service.is_selected,
      isSelectable: service.isSelectable || service.is_selectable // Map from either property
    }));
    
    return { 
      services: processedServices,
      approvedOfferId
    };
  } catch (err) {
    console.error("Error in fetchSelectedServicesForProposal:", err);
    return null;
  }
};

// Add proper types for the ThankYouPage component props
interface ThankYouPageProps {
  proposal: Proposal;
  business: Business;
  selectedOffer: string | null;
  supabase: any;
}

// Add a new DeclinePage component
interface DeclinePageProps {
  business: Business;
}

const DeclinePage: React.FC<DeclinePageProps> = ({ business }) => {
  return (
    <div className="min-h-screen bg-black">
      <div className="relative max-w-4xl mx-auto px-3 sm:px-6 py-8 sm:py-12">
        <div className="text-center space-y-6">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-red-500/20 text-red-400">
            <X className="w-8 h-8 sm:w-10 sm:h-10" />
          </div>
          
          <div className="space-y-2">
            <h2 className="text-2xl sm:text-3xl font-bold text-white font-orbitron">Proposal Declined</h2>
            <p className="text-blue-200/70 text-sm sm:text-base">
              You have declined all offers in this proposal. If you'd like to discuss other options, please contact us directly.
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-white/10 p-4 sm:p-6 mt-8">
            <h3 className="text-sm sm:text-base font-medium text-blue-100 mb-3">Next Steps</h3>
            <p className="text-blue-200/70 text-sm leading-relaxed">
              We appreciate you taking the time to review our proposal. If you have any questions or would like to discuss alternative solutions, please don't hesitate to reach out.
            </p>
          </div>

          <div className="text-center mt-8 text-xs sm:text-sm text-blue-200/70">
            <p className="space-y-1 sm:space-y-0">
              <span className="block sm:inline">Contact {business?.name || 'the business'}</span>
              {business?.email && (
                <span className="block sm:inline sm:ml-1">
                  at <a href={`mailto:${business.email}`} className="text-blue-400 hover:text-blue-300 transition-colors">{business.email}</a>
                  {business?.phone && (
                    <> or call at <a href={`tel:${business.phone}`} className="text-blue-400 hover:text-blue-300 transition-colors">{business.phone}</a></>
                  )}
                </span>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Update the ThankYouPage component with proper types
const ThankYouPage: React.FC<ThankYouPageProps> = ({ proposal, business, selectedOffer, supabase }) => {
  const [selectedServices, setSelectedServices] = useState<Service[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    // Function to fetch selected services
    const fetchSelectedServices = async () => {
      setIsLoading(true);
      try {
        console.log("Fetching selected services for proposal:", proposal.id);
        console.log("Selected offer ID:", selectedOffer);
        
        // Debug: First fetch ALL services for the proposal to compare
        const { data: allServicesData } = await supabase
          .from('proposal_services_with_details')
          .select('*')
          .eq('proposal_id', proposal.id);
          
        console.log("ALL services for this proposal:", allServicesData?.length, allServicesData);
        
        // Find the approved offer first
        let approvedOfferId = selectedOffer;
        if (!approvedOfferId) {
          // Try to find the approved offer from the proposal data
          const { data: approvedOfferData } = await supabase
            .from('proposal_offers')
            .select('id')
            .eq('proposal_id', proposal.id)
            .eq('status', 'approved')
            .single();
          approvedOfferId = approvedOfferData?.id;
        }
        
        if (!approvedOfferId) {
          console.log("No approved offer found for proposal:", proposal.id);
          setSelectedServices([]);
          setTotal(0);
          setIsLoading(false);
          return;
        }
        
        console.log("Using approved offer ID:", approvedOfferId);
        
        // Fetch ALL services from the approved offer, then filter appropriately
        const { data: offerServicesData, error: selectedServicesError } = await supabase
          .from('proposal_services_with_details')
          .select('*')
          .eq('proposal_id', proposal.id)
          .eq('offer_id', approvedOfferId);
          
        if (selectedServicesError) {
          console.error("Error fetching offer services:", selectedServicesError);
          throw selectedServicesError;
        }
        
        if (!offerServicesData || offerServicesData.length === 0) {
          console.log("No services found for approved offer:", approvedOfferId);
          setSelectedServices([]);
          setTotal(0);
          setIsLoading(false);
          return;
        }
        
        // Filter services: include all required services + selected optional services
        const selectedServicesData = offerServicesData.filter((service: any) => {
          const isOptional = service.is_selectable === true;
          const isSelected = service.is_selected === true;
          
          // Include if: (not optional) OR (optional AND selected)
          const shouldInclude = !isOptional || (isOptional && isSelected);
          
          console.log(`Service ${service.name}: optional=${isOptional}, selected=${isSelected}, include=${shouldInclude}`);
          return shouldInclude;
                 });
        
                  if (!selectedServicesData || selectedServicesData.length === 0) {
            console.log("No services to display for approved offer:", approvedOfferId);
            setSelectedServices([]);
            setTotal(0);
            setIsLoading(false);
            return;
          }
          
          console.log("Services to display from approved offer:", selectedServicesData.length, selectedServicesData);
        
                  // Debug: Log specific fields we care about for services to display
          selectedServicesData.forEach((service: any, index: number) => {
            console.log(`Service #${index + 1} - ${service.name || service.template_service_name}:`, {
              id: service.id,
              is_selected: service.is_selected,
              is_selectable: service.is_selectable,
              price: service.price,
              quantity: service.quantity,
              offer_id: service.offer_id,
              included_because: service.is_selectable ? 'optional and selected' : 'required service'
            });
          });
        
        // Process the services with complete details from the view
        const processedServices = selectedServicesData.map((service: any) => ({
          id: service.id,
          name: service.name || service.template_service_name, // Fallback to template name
          description: service.description || service.template_service_description || '',
          price: service.price || 0,
          quantity: service.quantity || 1,
          title: service.name || service.template_service_name, // Use name as title
          isSelectable: service.is_selectable,
          is_selected: true
        }));
        
        console.log("Processed services:", processedServices.length, processedServices);
        setSelectedServices(processedServices);
        
        // Calculate totals based on selected services
        const calculatedTotal = processedServices.reduce((sum: number, service: Service) => {
          return sum + (service.price * service.quantity);
        }, 0);
        
        console.log("Calculated total:", calculatedTotal);
        setTotal(calculatedTotal);
        
      } catch (error) {
        console.error("Error in fetchSelectedServices:", error);
        setError(error instanceof Error ? error.message : 'Failed to fetch selected services');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchSelectedServices();
  }, [proposal.id, supabase, selectedOffer]);

  // Calculate total amount safely with proper currency rounding
  const subtotal = Math.round(selectedServices.reduce(
    (sum, service) => sum + (typeof service.price === 'number' ? service.price : 0), 
    0
  ) * 100) / 100;
  
  // Calculate tax (GST) - default to 5% if not specified
  const tax = 0.05; // 5% GST
  const taxAmount = Math.round((subtotal * tax) * 100) / 100;
  const totalAmount = Math.round((subtotal + taxAmount) * 100) / 100;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-blue-100 mb-2 font-orbitron">Error</h2>
          <p className="text-blue-200/70">{error}</p>
        </div>
      </div>
    );
  }

  if (!proposal || !business) return null;

  // Show the decline page if status is declined
  if (proposal.status === 'declined') {
    return <DeclinePage business={business} />;
  }

  // Show thank you page if approved
  if (proposal.status === 'approved') {
    return (
      <div className="min-h-screen bg-black">
        <div className="relative max-w-4xl mx-auto px-3 sm:px-6 py-8 sm:py-12">
          <div className="text-center space-y-6">
            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#00CC4D]/20 text-[#00CC4D]">
              <svg className="w-8 h-8 sm:w-10 sm:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            
            <div className="space-y-2">
              <h2 className="text-2xl sm:text-3xl font-bold text-white font-orbitron">Thank You!</h2>
              <p className="text-blue-200/70 text-sm sm:text-base">
                We've received your selection and will reach out to you shortly to proceed with the next steps.
              </p>
              {error && (
                <p className="text-xs text-yellow-300 mt-1">Error: {error}</p>
              )}
            </div>

            <div className="bg-white/5 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-white/10 p-4 sm:p-6 mt-8">
              <h3 className="text-sm sm:text-base font-medium text-blue-100 mb-3">Selected Services</h3>
              
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
                </div>
              ) : selectedServices.length > 0 ? (
                <>
                  {selectedServices.map((service, index) => (
                    <div key={index} className="text-left mb-4 last:mb-0 pb-4 last:pb-0 border-b border-white/10 last:border-b-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-white font-medium">{service.name.replace(/ \(Optional\)/gi, '')}</p>
                          </div>
                                        {service.description && (
                <div className="mt-1">
                  <RichTextDescription 
                    description={service.description} 
                    className="prose-sm text-blue-200/70 prose-headings:text-blue-200 prose-strong:text-blue-100 prose-em:text-blue-300/70" 
                  />
                </div>
              )}
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-[#00CC4D]">
                            ${typeof service.price === 'number' ? service.price.toFixed(2) : '0.00'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm sm:text-base">
                        <span className="text-blue-200/70">Subtotal:</span>
                        <span className="text-[#00CC4D]">
                          ${subtotal.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm sm:text-base">
                        <span className="text-blue-200/70">GST ({(tax * 100).toFixed(0)}%):</span>
                        <span className="text-[#00CC4D]">
                          ${taxAmount.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm sm:text-base pt-2 border-t border-white/10">
                        <span className="text-blue-200/70 font-medium">Total:</span>
                        <span className="text-lg font-bold text-[#00CC4D]">
                          ${totalAmount.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-right text-blue-200/70 mt-1">inc. GST</p>
                  </div>
                </>
              ) : (
                <div className="text-center py-4">
                  <p className="text-blue-200/70">
                    {error || 'No services selected'}
                  </p>
                </div>
              )}
            </div>

            <div className="text-center mt-8 text-xs sm:text-sm text-blue-200/70">
              <p className="space-y-1 sm:space-y-0">
                <span className="block sm:inline">If you have any questions, contact {business?.name || 'the business'}</span>
                {business?.email && (
                  <span className="block sm:inline sm:ml-1">
                    at <a href={`mailto:${business.email}`} className="text-blue-400 hover:text-blue-300 transition-colors">{business.email}</a>
                    {business?.phone && (
                      <> or call at <a href={`tel:${business.phone}`} className="text-blue-400 hover:text-blue-300 transition-colors">{business.phone}</a></>
                    )}
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// Fix the missing type for the isSelectedServiceToggle state
interface ServiceToggleState {
  [id: string]: boolean;
}

// Update the PublicProposal component
const PublicProposal: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [expandedOffers, setExpandedOffers] = useState<string[]>([]);
  const [selectedOffer, setSelectedOffer] = useState<string | null>(null);
  const [selectedOptionalServices, setSelectedOptionalServices] = useState<Set<string>>(new Set());
  const [directlyFetchedServices, setDirectlyFetchedServices] = useState<any[]>([]);
  const [isSelectedServiceToggle, setIsSelectedServiceToggle] = useState<ServiceToggleState>({});

  // Helper function to directly update a service selection state
  const updateServiceSelection = async (serviceId: string, isSelected: boolean) => {
    if (!supabase) return false;
    
    try {
      const { error } = await supabase
        .from('proposal_services')
        .update({ is_selected: isSelected })
        .eq('id', serviceId);
        
      return !error;
    } catch (err) {
      console.error(`Error updating service ${serviceId}:`, err);
      return false;
    }
  };

  // Update the handleToggleSelectableService function with proper types
  const handleToggleSelectableService = (serviceId: string) => {
    setIsSelectedServiceToggle(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
    
    // Update the services array with the new toggle state
    setProposal(prevProposal => {
      if (!prevProposal) return prevProposal;
      
      const updatedServices = prevProposal.services.map(service => {
        if (service.id === serviceId) {
          return {
            ...service,
            is_selected: !isSelectedServiceToggle[serviceId]
          };
        }
        return service;
      });
      
      return {
        ...prevProposal,
        services: updatedServices
      };
    });
  };

  // Fix any remaining typing issues in the renderService function
  const renderService = (service: Service) => {
    // ... existing code ...
  };
  
  // Update the handleAcceptOffer function with proper types
  const handleAcceptOffer = async (offerId: string) => {
    // ... existing code ...
  };

  // Add a function to ensure optional services are properly initialized
  const resetOptionalServiceSelections = async (services: Service[]) => {
    if (!supabase) return;
    
    // For each optional service, ensure it's initially not selected in the database
    for (const service of services) {
      if (!service.isSelectable || !service.id || service.is_selected === false) continue;
      
      // If it's an optional service and not explicitly set to false in the database,
      // make sure it's set to false
      console.log(`Resetting optional service ${service.id} to unselected state`);
      
      try {
        const { error } = await supabase.rpc('update_service_selection', {
          p_service_id: service.id,
          p_is_selected: false
        });
        
        if (error) {
          console.error(`Error resetting service ${service.id}:`, error);
        }
      } catch (err) {
        console.error(`Error calling service reset:`, err);
      }
    }
  };

  const fetchProposalData = async () => {
    try {
      console.log('Starting to fetch proposal data with token:', token);
      
      if (!token) {
        console.error('No token provided in URL');
        setError('No proposal token provided');
        setIsLoading(false);
        return;
      }

      if (!supabase) {
        console.error('Supabase client is not initialized');
        setError('Database connection is not available');
        setIsLoading(false);
        return;
      }

      // FIXED: Direct token validation (no session dependency)
      // Get the token data first to validate it and get proposal_id
      console.log('Fetching token data...');
      const { data: tokenDataArray, error: tokenError } = await supabase
        .from('public_proposal_tokens')
        .select('*, proposals!inner(valid_until)')
        .eq('token', token)
        .order('created_at', { ascending: false })
        .limit(1);

      if (tokenError) {
        console.error('Error fetching token data:', tokenError);
        setError('Invalid proposal token - please check your link');
        setIsLoading(false);
        return;
      }

      if (!tokenDataArray || tokenDataArray.length === 0) {
        console.error('No token data found for token:', token);
        setError('Invalid proposal token - link not found');
        setIsLoading(false);
        return;
      }

      const tokenData = tokenDataArray[0];
      console.log('Token data found:', tokenData);

      // Check if token is expired
      const tokenExpired = new Date(tokenData.expires_at) < new Date();
      const proposalExpired = tokenData.proposals?.valid_until && new Date(tokenData.proposals.valid_until) < new Date();

      if (tokenExpired || proposalExpired) {
        console.error('Token or proposal expired:', { 
          tokenExpired, 
          proposalExpired,
          tokenExpiresAt: tokenData.expires_at,
          proposalExpiresAt: tokenData.proposals?.valid_until 
        });
        setError('Proposal link has expired');
        setExpired(true);
        setIsLoading(false);
        return;
      }

      // Get proposal details with latest service selection states (using new RLS policies)
      console.log('Fetching proposal details for proposal_id:', tokenData.proposal_id);
      
      // First, try a simple query to see if the proposal exists
      const { data: basicProposalArray, error: basicError } = await supabase
        .from('proposals')
        .select('*')
        .eq('id', tokenData.proposal_id)
        .limit(1);
        
      console.log('Basic proposal query result:', { data: basicProposalArray, error: basicError });
      
      if (basicError || !basicProposalArray || basicProposalArray.length === 0) {
        console.error('Basic proposal query failed:', basicError);
        setError('Proposal not found - please check your link');
        setIsLoading(false);
        return;
      }
      
      // Now try the complex query with relationships
      const { data: proposalDataArray, error: proposalError } = await supabase
        .from('proposals')
        .select(`
          *,
          customer:customers(*)
        `)
        .eq('id', tokenData.proposal_id)
        .limit(1);

      if (proposalError) {
        console.error('Error fetching proposal details:', proposalError);
        setError(`Proposal details not found: ${proposalError.message}`);
        setIsLoading(false);
        return;
      }

      if (!proposalDataArray || proposalDataArray.length === 0) {
        console.error('No proposal data found for id:', tokenData.proposal_id);
        setError('Proposal details not found: No data returned');
        setIsLoading(false);
        return;
      }

      const proposalData = proposalDataArray[0];

      console.log('Proposal data found:', proposalData);
      
      // Fetch services separately with their offer relationships
      console.log('Fetching services for proposal...');
      const { data: servicesData, error: servicesError } = await supabase
        .from('proposal_services')
        .select(`
          *,
          offer:proposal_offers(
            id,
            title,
            status
          )
        `)
        .eq('proposal_id', tokenData.proposal_id);
        
      console.log('Services query result:', { data: servicesData, error: servicesError });
      
      if (servicesError) {
        console.error('Error fetching services:', servicesError);
        // Continue without services for now
        proposalData.services = [];
      } else {
        proposalData.services = servicesData || [];
      }
      
      console.log('Final proposal with services:', proposalData);
      console.log('Services with selection states:', proposalData.services);

      // Check if proposal itself has expired (in addition to token expiry check)
      if (proposalData.valid_until && new Date(proposalData.valid_until) < new Date()) {
        console.error('Proposal expired:', proposalData.valid_until);
        setError('This proposal has expired and is no longer available');
        setExpired(true);
        setIsLoading(false);
        return;
      }

      // If the proposal is approved, find and set the selected offer
      if (proposalData.status === 'approved') {
        const approvedService = proposalData.services.find((service: any) => service.offer?.status === 'approved');
        if (approvedService?.offer?.id) {
          setSelectedOffer(approvedService.offer.id);
          console.log(`Found approved offer: ${approvedService.offer.id}`);
        }
      }

      // Get business details
      console.log('Fetching business details for business_id:', tokenData.business_id);
      const { data: businessDataArray, error: businessError } = await supabase
        .from('businesses')
        .select('*')
        .eq('id', tokenData.business_id)
        .limit(1);

      if (businessError) {
        console.error('Error fetching business details:', businessError);
        setError(`Business information not found: ${businessError.message}`);
        setIsLoading(false);
        return;
      }

      if (!businessDataArray || businessDataArray.length === 0) {
        console.error('No business data found for id:', tokenData.business_id);
        setError('Business information not found: No data returned');
        setIsLoading(false);
        return;
      }

      const businessData = businessDataArray[0];

      console.log('Business data found:', businessData);
      
      // Only update proposal status from 'sent' to 'viewed' once
      // The database trigger will automatically log the view event
      if (proposalData.status === 'sent') {
        console.log('Updating proposal status to viewed...');
        const { error: updateError } = await supabase
          .from('proposals')
          .update({ status: 'viewed' })
          .eq('id', proposalData.id);
          
        if (updateError) {
          console.error('Error updating proposal status:', updateError);
          // Continue even if update fails
        } else {
          // Update local status
          proposalData.status = 'viewed';
          
          // Create proposal viewed milestone
          try {
            await createProposalViewedMilestone(
              tokenData.business_id,
              proposalData.customer_id,
              proposalData.id,
              proposalData.title,
              new Date().toISOString()
            );
            console.log('✅ Proposal viewed milestone created successfully');
          } catch (milestoneError) {
            console.error('❌ Error creating proposal viewed milestone:', milestoneError);
            // Continue even if milestone creation fails
          }
        }
      } else {
        // For proposals already viewed, approved, etc., log a view event
        // This ensures we track each time the customer views the proposal
        try {
          await logProposalView(
            proposalData.id,
            tokenData.business_id,
            token,
            proposalData.customer_id,
            { proposal_status: proposalData.status }
          );
        } catch (logError) {
          console.error('Error logging repeat view:', logError);
          // Continue even if logging fails
        }
      }
      
      setProposal(proposalData);
      setBusiness(businessData);
      setIsLoading(false);

      // Find all unique offer IDs and expand them
      const offerIds = proposalData.services.reduce((ids: string[], service: any) => {
        if (service.offer?.id && !ids.includes(service.offer.id)) {
          ids.push(service.offer.id);
        }
        return ids;
      }, [] as string[]);
      
      setExpandedOffers(offerIds);
    } catch (err) {
      console.error('Error in fetchProposalData:', err);
      setError(`An unexpected error occurred: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setIsLoading(false);
    }
  };

  const handleToggleOffer = (offerId: string) => {
    setExpandedOffers(prev =>
      prev.includes(offerId)
        ? prev.filter(id => id !== offerId)
        : [...prev, offerId]
    );
  };

  const handleSelectOffer = async (offerId: string) => {
    if (!proposal || !supabase) {
      console.error("Cannot select offer: supabase client or proposal is null");
      return;
    }

    try {
      // Get all services for this offer
      const offerServices = proposal.services.filter(service => 
        service.offer?.id === offerId
      );
      
      console.log(`Approving offer ${offerId} with ${offerServices.length} services`);
      
      // Log selection state of all services, highlighting optional ones
      console.log("Service selection states:");
      offerServices.forEach((service, idx) => {
        console.log(`Service #${idx + 1} - ${service.name}:`, {
          id: service.id,
          isOptional: service.isSelectable === true,
          is_selected: service.is_selected,
          price: service.price,
          quantity: service.quantity
        });
      });
      
      // Ensure all optional services have correct selection state
      const optionalServices = offerServices.filter(service => service.isSelectable === true);
      console.log(`This offer has ${optionalServices.length} optional services`);
      
      // First update the proposal status to approved
      const { error: proposalError } = await supabase
        .from('proposals')
        .update({
          status: 'approved',
          notes: JSON.stringify({
            approved_offer_id: offerId,
            approved_at: new Date().toISOString(),
          })
        })
        .eq('id', proposal.id);

      if (proposalError) {
        console.error("Error updating proposal status:", proposalError);
        throw proposalError;
      }

      // Update the selected offer status to approved
      const { error: offerError } = await supabase
        .from('proposal_offers')
        .update({ status: 'approved' })
        .eq('id', offerId);

      if (offerError) {
        console.error("Error updating offer status:", offerError);
        throw offerError;
      }

      // Update other offers to declined
      const { error: declineError } = await supabase
        .from('proposal_offers')
        .update({ status: 'declined' })
        .neq('id', offerId)
        .eq('proposal_id', proposal.id);

      if (declineError) {
        console.error("Error declining other offers:", declineError);
        throw declineError;
      }
      
      // Create proposal approved milestone
      try {
        // Get the approved offer title
        const approvedOffer = offerServices[0]?.offer;
        await createProposalApprovedMilestone(
          proposal.business_id,
          proposal.customer_id,
          proposal.id,
          proposal.title,
          approvedOffer?.title
        );
        console.log('✅ Proposal approved milestone created successfully');
      } catch (milestoneError) {
        console.error('❌ Error creating proposal approved milestone:', milestoneError);
        // Continue even if milestone creation fails
      }
      
      // No need to update service selection states - they're already set by the toggle switches
      
      // Update local state
      setSelectedOffer(offerId);
      setProposal({
        ...proposal,
        status: 'approved',
        notes: JSON.stringify({
          approved_offer_id: offerId,
          approved_at: new Date().toISOString(),
        }),
        services: proposal.services.map(service => {
          if (!service.offer) return service;
          
          return {
            ...service,
            offer: {
              ...service.offer,
              status: service.offer.id === offerId ? 'approved' : 'declined'
            }
            // is_selected stays the same as already set in the database
          };
        })
      });
      
      console.log("Offer selection complete. Local state updated.");
      
      // Instead of reloading, force ThankYouPage to render
      setProposal({
        ...proposal,
        status: 'approved'
      });
      // No need to reload the page
      // window.location.reload();
      
    } catch (err) {
      console.error('Error selecting offer:', err);
      setSelectedOffer(null);
      alert(`Failed to select offer: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleDeclineAll = async () => {
    if (!proposal || !supabase) return;

    try {
      const { error } = await supabase
        .from('proposals')
        .update({ status: 'declined' })
        .eq('id', proposal.id);

      if (error) throw error;
      
      
      setProposal({ ...proposal, status: 'declined' });
    } catch (err) {
      console.error('Error declining proposal:', err);
    }
  };

  // Add useEffect for direct fetch of selected services
  useEffect(() => {
    if (proposal?.status === 'approved' && supabase) {
      console.log('Approved proposal detected, fetching selected services directly...');
      
      // Fetch selected services
      (async () => {
        const result = await fetchSelectedServicesForProposal(supabase, proposal.id);
        if (result && result.services.length > 0) {
          console.log("Successfully fetched services directly in useEffect:", result.services);
          setDirectlyFetchedServices(result.services);
        }
      })();
    }
  }, [proposal?.id, proposal?.status, supabase]);

  useEffect(() => {
    fetchProposalData();
  }, [token]);

  // Function to handle toggle of optional service
  const handleToggleOptionalService = async (serviceId: string) => {
    if (!proposal || !supabase) {
      console.error("Cannot toggle service: supabase client or proposal is null");
      return;
    }
    
    try {
      // Find the service in our local state
      const service = proposal.services.find((s: any) => s.id === serviceId);
      if (!service) {
        console.error(`Service ${serviceId} not found in proposal`);
        return;
      }
      
      // Get the current selection state and toggle it
      const currentlySelected = service.is_selected === true;
      const newSelectionState = !currentlySelected;
      
      console.log(`Toggling service ${serviceId} (${service.name}) from ${currentlySelected} to ${newSelectionState}`);
      
      // Update local state immediately for responsive UI
      setProposal({
        ...proposal,
        services: proposal.services.map((s: any) => 
          s.id === serviceId 
            ? { ...s, is_selected: newSelectionState } 
            : s
        )
      });
      
      // Update the database
      const { error } = await supabase
        .from('proposal_services')
        .update({ is_selected: newSelectionState })
        .eq('id', serviceId);
        
      if (error) {
        console.error(`Error updating service ${serviceId}:`, error);
        
        // Revert local state if there was an error
        setProposal({
          ...proposal,
          services: proposal.services.map((s: any) => 
            s.id === serviceId 
              ? { ...s, is_selected: currentlySelected } 
              : s
          )
        });
        
        alert(`Failed to toggle service: ${error.message}`);
      } else {
        console.log(`Service ${serviceId} successfully updated in database`);
        
        // Verify the update with a direct query
        const { data: verifyDataArray, error: verifyError } = await supabase
          .from('proposal_services')
          .select('is_selected')
          .eq('id', serviceId)
          .limit(1);
        
        const verifyData = verifyDataArray?.[0];
          
        if (verifyError) {
          console.error(`Error verifying service update for ${serviceId}:`, verifyError);
        } else if (verifyData) {
          console.log(`Verified service ${serviceId} is now ${verifyData.is_selected ? 'selected' : 'unselected'}`);
          if (verifyData.is_selected !== newSelectionState) {
            console.warn(`Verification failed: expected ${newSelectionState} but got ${verifyData.is_selected}`);
          }
        }
      }
    } catch (err) {
      console.error('Error toggling service selection:', err);
      alert(`An error occurred: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-blue-100 mb-2 font-orbitron">Error</h2>
          <p className="text-blue-200/70">{error}</p>
        </div>
      </div>
    );
  }

  if (!proposal || !business) return null;

  // Show the decline page if status is declined
  if (proposal.status === 'declined') {
    return <DeclinePage business={business} />;
  }

  // Show thank you page if approved
  if (proposal.status === 'approved') {
    return (
      <ThankYouPage 
        proposal={proposal} 
        business={business} 
        selectedOffer={selectedOffer} 
        supabase={supabase}
      />
    );
  }

  // In the return statement, change how offers are grouped and mapped
  const renderOffers = () => {
    if (!proposal) return null;

    // Group services by offer ID
    const offerGroups: Record<string, Service[]> = {};
    
    proposal.services?.forEach(service => {
      if (service.offer?.id) {
        if (!offerGroups[service.offer.id]) {
          offerGroups[service.offer.id] = [];
        }
        offerGroups[service.offer.id].push(service);
      }
    });

    // Convert grouping to array for rendering
    const groupedOffers = Object.entries(offerGroups).map(([offerId, services]) => ({
      id: offerId,
      title: services[0]?.offer?.title || "Offer",
      services: services
    }));

    return (
      <div className="space-y-3 sm:space-y-4">
        {groupedOffers.map(offer => (
          <Offer
            key={offer.id}
            title={offer.title}
            services={offer.services}
            isExpanded={expandedOffers.includes(offer.id)}
            isSelected={selectedOffer === offer.id}
            onToggle={() => handleToggleOffer(offer.id)}
            onSelect={() => handleSelectOffer(offer.id)}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black text-blue-100 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,_rgba(17,24,39,1),rgba(0,0,0,1))]" />
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-green-500/10" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent" />
      </div>

      {/* Business Header */}
      <div className="relative">
        <div className="bg-black/40 backdrop-blur-xl border-b border-white/10">
          <div className="max-w-4xl mx-auto px-3 sm:px-6 py-4 sm:py-8">
            <div className="flex flex-col items-center text-center mb-3 sm:mb-6">
              {business?.logo_url && (
                <img
                  src={business.logo_url}
                  alt={business.name}
                  className="h-12 sm:h-16 w-12 sm:w-16 object-contain rounded-lg sm:rounded-xl mb-3 sm:mb-4 bg-black/50 p-1.5 sm:p-2 border border-white/10"
                />
              )}
              <div>
                <h1 className="text-lg sm:text-2xl font-bold text-white font-orbitron">{business?.name || 'Business Name'}</h1>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative max-w-4xl mx-auto px-3 sm:px-6 py-4 sm:py-8">
        <div className="mb-4 sm:mb-8">
          <p className="text-center text-blue-100 bg-white/5 backdrop-blur-xl py-2.5 sm:py-4 px-3 sm:px-6 rounded-xl sm:rounded-2xl border border-white/10 text-xs sm:text-base">
            Select 1 of the {Object.keys(proposal.services?.reduce((acc: Record<string, boolean>, service: any) => {
              if (service.offer?.id) acc[service.offer.id] = true;
              return acc;
            }, {} as Record<string, boolean>) || {}).length} offers you would like to move forward with below
          </p>
          
          {/* Temporary Debug Info */}
          <div className="mt-4 bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4 text-xs">
            <details>
              <summary className="cursor-pointer text-yellow-300 mb-2">🔍 Debug: Proposal Data Structure</summary>
              <div className="text-yellow-100 space-y-2">
                <div><strong>Services Count:</strong> {proposal.services?.length || 0}</div>
                <div><strong>Services:</strong></div>
                <pre className="text-xs bg-black/30 p-2 rounded overflow-auto max-h-40">
                  {JSON.stringify(proposal.services?.map(s => ({
                    id: s.id,
                    name: s.name,
                    price: s.price,
                    offer: s.offer
                  })) || [], null, 2)}
                </pre>
              </div>
            </details>
          </div>
        </div>

        {renderOffers()}

        {proposal.status === 'sent' && (
          <div className="text-center mt-6 sm:mt-12">
            <p className="text-xs sm:text-sm text-blue-200/70 mb-3 sm:mb-6 bg-white/5 backdrop-blur-xl py-2.5 sm:py-4 px-3 sm:px-6 rounded-xl sm:rounded-2xl border border-white/10">
              If you choose not to select any of the offers above, please click 'Decline All' below.
            </p>
            <button
              onClick={handleDeclineAll}
              className="
                inline-flex items-center px-4 sm:px-6 py-1.5 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium
                bg-red-500/20 text-red-400 border border-red-500/30
                hover:bg-red-500/30 transition-all duration-300
                font-orbitron
              "
            >
              <X className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
              DECLINE ALL
            </button>
          </div>
        )}

        <div className="text-center mt-6 sm:mt-12 text-xs sm:text-sm text-blue-200/70 bg-white/5 backdrop-blur-xl py-2.5 sm:py-4 px-3 sm:px-6 rounded-xl sm:rounded-2xl border border-white/10">
          <p className="space-y-1 sm:space-y-0">
            <span className="block sm:inline">If you have any questions, contact {business.name}</span>
            <span className="block sm:inline sm:ml-1">
              at <a href={`mailto:${business.email}`} className="text-blue-400 hover:text-blue-300 transition-colors">{business.email}</a>
              {' '}or call at{' '}
              <a href={`tel:${business.phone}`} className="text-blue-400 hover:text-blue-300 transition-colors">{business.phone}</a>
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}

export default PublicProposal;


